// ical-codec.js — minimal RFC 5545 (iCalendar) parser + serialiser.
//
// Scope is deliberately narrow: just enough VEVENT support to round-trip
// title, start/end, all-day, RRULE, EXDATE, RECURRENCE-ID, UID, SEQUENCE,
// and LAST-MODIFIED through CalDAV. The codebase has no other iCal
// consumer; bringing in a full RFC 5545 library would be 100+ KB of dead
// weight for properties (VTODO, VJOURNAL, VTIMEZONE catalog, ATTACH, …)
// we don't surface anywhere.
//
// Used by:
//   • caldav-provider.js — parse calendar-multiget responses, build PUT bodies
//   • outbox.js          — serialise queued writes so they survive restart
//   • Phase 5 RRULE      — local occurrence expansion via expandRrule()
//
// What we deliberately skip:
//   • VTIMEZONE — we coerce to UTC on serialise and rely on TZID lookup via
//     the host's Intl support on parse. Most consumer calendars use TZID +
//     a VTIMEZONE block; we read the TZID name and let JS handle conversion.
//     If a TZID is unknown we fall back to floating local time.
//   • VALARM    — out of scope (no notifications surface yet).
//   • Properties beyond the whitelist below — preserved as raw lines under
//     `_extras` so a round-trip PUT doesn't strip user content.

// ── Codec data shapes ────────────────────────────────────────────────────

/** A parsed `NAME;param=val:value` content line. */
interface ParsedLine {
  name: string;
  params: Record<string, string>;
  value: string;
}

/** A parsed DATE / DATE-TIME value, normalised toward UTC ISO. */
interface ParsedDt {
  iso: string | null;
  allDay: boolean;
  tzid?: string;
}

/** A normalised calendar event (one per VEVENT). Built incrementally by the
 *  parser, so most fields stay absent until their property is seen; the
 *  `_`-prefixed fields are codec-internal round-trip carriers. */
export interface ICalEvent {
  providerId?: string;
  title?: string;
  description?: string;
  location?: string;
  startDate?: string | null;
  endDate?: string | null;
  allDay?: boolean;
  rrule?: string;
  exdate?: (string | null)[];
  recurrenceId?: string | null;
  sequence?: number;
  lastModified?: string | null;
  status?: string;
  transparency?: string;
  created?: string | null;
  _startTzid?: string;
  _endTzid?: string;
  _duration?: string;
  _extras?: ParsedLine[];
}

// ── Line folding (RFC 5545 §3.1) ──────────────────────────────────────
// Content lines are folded after 75 octets with a CRLF + single whitespace
// continuation. We unfold on parse and re-fold on serialise so downstream
// string handling stays naive.
/**
 * @param {string} raw
 * @returns {string}
 */
function _unfold(raw: string): string {
  // Treat lone LF as CRLF for tolerance with hand-written fixtures.
  return raw.replace(/\r?\n[ \t]/g, '');
}

/**
 * @param {string} line
 * @returns {string}
 */
function _fold(line: string): string {
  if (line.length <= 75) return line;
  const out = [line.slice(0, 75)];
  let rest = line.slice(75);
  while (rest.length > 74) {
    out.push(` ${rest.slice(0, 74)}`);
    rest = rest.slice(74);
  }
  if (rest.length) out.push(` ${rest}`);
  return out.join('\r\n');
}

// ── Property line parser ──────────────────────────────────────────────
// RFC 5545 §3.2: NAME[;PARAM=VALUE[,…]]:VALUE
/**
 * @param {string} line
 * @returns {{ name: string, params: Record<string, string>, value: string } | null}
 */
function _parseLine(line: string): ParsedLine | null {
  const colon = line.indexOf(':');
  if (colon < 0) return null;
  const head = line.slice(0, colon);
  const value = line.slice(colon + 1);
  const parts = head.split(';');
  const name = parts[0].toUpperCase();
  const params: Record<string, string> = {};
  for (let i = 1; i < parts.length; i++) {
    const eq = parts[i].indexOf('=');
    if (eq < 0) continue;
    params[parts[i].slice(0, eq).toUpperCase()] = parts[i].slice(eq + 1);
  }
  return { name, params, value };
}

// ── TZID resolution via the host Intl tz database ─────────────────────
// We don't carry a VTIMEZONE catalog (deliberate — see the file header). Instead
// we resolve a `DTSTART;TZID=America/New_York:…` wall-clock to a real UTC instant
// through `Intl`, which ships the full IANA tz database (DST rules included) in
// every host we target. Any VTIMEZONE block the peer sends is ignored on parse
// (parseICS only reads VEVENT props) — the TZID name is all we need.

// UTC offset in ms (local − UTC) of an IANA zone at a given instant. Formats the
// instant in the zone, reads the wall-clock back as if it were UTC, and diffs —
// the standard Intl offset probe. `hour: '2-digit', hour12: false` can yield "24"
// for midnight on some hosts, so normalise the hour mod 24.
// One Intl.DateTimeFormat per tzid. Constructing a DateTimeFormat is among the
// most expensive runtime operations; the RRULE expander calls _tzOffsetMsAt
// twice per occurrence (and again per loop probe), so a daily/weekly tzid event
// over a wide window would otherwise build hundreds of identical formatters.
// Options are invariant across calls — only tzid varies — so the instance is
// fully reusable.
const _tzDtfCache = new Map<string, Intl.DateTimeFormat>();
function _tzFormatter(tzid: string): Intl.DateTimeFormat {
  let dtf = _tzDtfCache.get(tzid);
  if (!dtf) {
    dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tzid,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    _tzDtfCache.set(tzid, dtf);
  }
  return dtf;
}
function _tzOffsetMsAt(tzid: any, utcMs: any): number {
  const dtf = _tzFormatter(tzid);
  const p: any = {};
  for (const part of dtf.formatToParts(new Date(utcMs))) p[part.type] = part.value;
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
  return asUtc - utcMs;
}

// Convert a wall-clock time expressed in `tzid` to a UTC ISO string. Returns null
// if `tzid` is unknown to the host (Intl throws → caller falls back to floating
// local). DST-boundary safe: re-probe the offset at the candidate UTC instant,
// since a wall-clock near a spring-forward / fall-back transition can pick the
// wrong offset on the first guess.
function _zonedToUtcIso(
  y: any,
  mo: any,
  d: any,
  h: any,
  mi: any,
  s: any,
  tzid: any
): string | null {
  try {
    const guess = Date.UTC(y, mo - 1, d, h, mi, s); // wall-clock read as if it were UTC
    const off1 = _tzOffsetMsAt(tzid, guess);
    let utc = guess - off1;
    const off2 = _tzOffsetMsAt(tzid, utc);
    if (off2 !== off1) utc = guess - off2;
    const iso = new Date(utc).toISOString();
    return Number.isNaN(utc) ? null : iso;
  } catch {
    return null;
  }
}

// ── DT parsing ────────────────────────────────────────────────────────
// Supports DATE (YYYYMMDD), DATE-TIME (YYYYMMDDTHHMMSS[Z]).
// Returns { iso, allDay, tzid? }.
/**
 * @param {string} value
 * @param {any} [params] - parsed property params (TZID, VALUE, …)
 * @returns {{ iso: string | null, allDay: boolean, tzid?: string }}
 */
function _parseDt(value: string, params: Record<string, string>): ParsedDt {
  const tzid = params?.TZID;
  const isDate = (params && params.VALUE === 'DATE') || /^\d{8}$/.test(value);
  if (isDate) {
    const yyyy = value.slice(0, 4),
      mm = value.slice(4, 6),
      dd = value.slice(6, 8);
    return { iso: `${yyyy}-${mm}-${dd}T00:00:00.000Z`, allDay: true };
  }
  // DATE-TIME
  const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z)?$/.exec(value);
  if (!m) return { iso: null, allDay: false, tzid };
  const [_, yyyy, mo, dd, hh, mi, ss, z] = m;
  if (z) {
    return { iso: `${yyyy}-${mo}-${dd}T${hh}:${mi}:${ss}.000Z`, allDay: false };
  }
  // Floating or TZID — best-effort: treat as local if no TZID, otherwise
  // construct via Intl. This is intentionally simplistic; production
  // RRULE workloads should normalise to UTC server-side.
  if (tzid) {
    // Resolve the TZID to a real UTC instant via the host's Intl tz data
    // (full IANA database, DST-aware). For a TZID the host doesn't know
    // (non-IANA / typo'd) Intl throws and we fall back to the naive
    // wall-clock treated as local — the previous best-effort behaviour.
    const utcIso = _zonedToUtcIso(+yyyy, +mo, +dd, +hh, +mi, +ss, tzid);
    if (utcIso) return { iso: utcIso, allDay: false, tzid };
    return { iso: `${yyyy}-${mo}-${dd}T${hh}:${mi}:${ss}.000`, allDay: false, tzid };
  }
  return { iso: `${yyyy}-${mo}-${dd}T${hh}:${mi}:${ss}.000`, allDay: false };
}

/**
 * @param {string | null} iso
 * @param {boolean} [allDay]
 * @returns {string | null}
 */
function _formatDt(iso: string | null | undefined, allDay?: boolean): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  if (allDay) {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${y}${m}${dd}`;
  }
  return d
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
  // e.g. 20260520T143000Z
}

// ── Text escaping (RFC 5545 §3.3.11) ──
/**
 * @param {any} s
 * @returns {string}
 */
function _escText(s: string): string {
  if (s == null) return '';
  return String(s)
    .replace(/\\/g, '\\\\')
    .replace(/\r?\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}
/**
 * @param {any} s
 * @returns {string}
 */
function _unescText(s: string): string {
  if (s == null) return '';
  // Single left-to-right pass so each backslash escape is consumed exactly
  // once. The previous staged replaces decoded `\n` before `\\`, which
  // mis-decoded an escaped backslash followed by 'n' (wire `\\n` → should be
  // `\` + literal 'n') into `\` + newline.
  return String(s).replace(/\\([\\;,nN])/g, (_m, c) => (c === 'n' || c === 'N' ? '\n' : c));
}

// ── VCALENDAR / VEVENT parsing ────────────────────────────────────────
// Returns an array of normalised event objects. Each VEVENT becomes one
// entry; recurring masters and their RECURRENCE-ID overrides are returned
// as separate entries with `recurrenceId` set on overrides.
/**
 * @param {string} text - raw VCALENDAR text.
 * @returns {any[]} normalised event objects (one per VEVENT).
 */
export function parseICS(text: string): ICalEvent[] {
  if (!text) return [];
  const lines = _unfold(String(text)).split(/\r?\n/);
  const events: ICalEvent[] = [];
  let cur: ICalEvent | null = null;
  const depth = []; // stack of component names so nested VALARM doesn't leak

  for (const raw of lines) {
    if (!raw) continue;
    const line = _parseLine(raw);
    if (!line) continue;
    if (line.name === 'BEGIN') {
      depth.push(line.value.toUpperCase());
      if (line.value.toUpperCase() === 'VEVENT') cur = { _extras: [] };
      continue;
    }
    if (line.name === 'END') {
      const closed = depth.pop();
      if (closed === 'VEVENT' && cur) {
        events.push(_finaliseEvent(cur));
        cur = null;
      }
      continue;
    }
    if (!cur || depth[depth.length - 1] !== 'VEVENT') continue; // skip non-VEVENT
    _applyProp(cur, line);
  }
  return events;
}

/**
 * @param {any} evt - the in-progress event accumulator.
 * @param {{ name: string, params: any, value: string }} prop
 */
function _applyProp(evt: any, { name, params, value }: ParsedLine) {
  switch (name) {
    case 'UID':
      evt.providerId = value;
      break;
    case 'SUMMARY':
      evt.title = _unescText(value);
      break;
    case 'DESCRIPTION':
      evt.description = _unescText(value);
      break;
    case 'LOCATION':
      evt.location = _unescText(value);
      break;
    case 'DTSTART': {
      const { iso, allDay, tzid } = _parseDt(value, params);
      evt.startDate = iso;
      evt.allDay = !!allDay;
      if (tzid) evt._startTzid = tzid;
      break;
    }
    case 'DTEND': {
      const { iso, allDay } = _parseDt(value, params);
      evt.endDate = iso;
      if (allDay && evt.allDay !== false) evt.allDay = true;
      break;
    }
    case 'DURATION':
      evt._duration = value;
      break;
    case 'RRULE':
      evt.rrule = value;
      break;
    case 'EXDATE':
      (evt.exdate = evt.exdate || []).push(_parseDt(value, params).iso);
      break;
    case 'RECURRENCE-ID':
      evt.recurrenceId = _parseDt(value, params).iso;
      break;
    case 'SEQUENCE':
      evt.sequence = parseInt(value, 10) || 0;
      break;
    case 'LAST-MODIFIED':
      evt.lastModified = _parseDt(value, {}).iso;
      break;
    case 'STATUS':
      evt.status = value;
      break;
    case 'TRANSP':
      evt.transparency = value;
      break;
    case 'CREATED':
      evt.created = _parseDt(value, {}).iso;
      break;
    default:
      // Preserve unknown lines so a round-trip PUT doesn't strip user data.
      evt._extras.push({ name, params, value });
  }
}

// Parse an RFC 5545 DURATION value (e.g. `PT1H`, `P1DT2H30M`, `P1W`, `-PT15M`)
// into milliseconds. Returns null if the value isn't a well-formed duration.
/**
 * @param {string} dur
 * @returns {number | null}
 */
function _parseDuration(dur: string): number | null {
  if (!dur) return null;
  const m = /^([+-])?P(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/.exec(
    dur.trim()
  );
  if (!m || (!m[2] && !m[3] && !m[4] && !m[5] && !m[6])) return null;
  const sign = m[1] === '-' ? -1 : 1;
  const weeks = +(m[2] || 0),
    days = +(m[3] || 0),
    hours = +(m[4] || 0),
    mins = +(m[5] || 0),
    secs = +(m[6] || 0);
  return sign * ((((weeks * 7 + days) * 24 + hours) * 60 + mins) * 60 + secs) * 1000;
}

/**
 * @param {any} evt
 * @returns {any}
 */
function _finaliseEvent(evt: any): ICalEvent {
  if (!evt.title) evt.title = '';
  if (!evt.allDay) evt.allDay = false;
  // RFC 5545 lets a VEVENT carry its end as DTEND *or* DURATION (or neither —
  // a zero-length timed event / a one-day all-day event). Derive endDate when
  // it's absent: calendar-sync drops any event without an endDate, so leaving
  // it unset silently omits standards-valid events (CalDAV/iCloud commonly emit
  // DURATION instead of DTEND). All-day DTEND stays exclusive (+1 day), matching
  // the convention downstream code expects.
  if (!evt.endDate && evt.startDate) {
    const startMs = Date.parse(evt.startDate);
    if (!Number.isNaN(startMs)) {
      let durMs = evt._duration ? _parseDuration(evt._duration) : null;
      if (durMs == null) durMs = evt.allDay ? 86400000 : 0;
      evt.endDate = new Date(startMs + durMs).toISOString();
    }
  }
  return evt;
}

// ── Serialise one event ─────────────────────────────────────────────────
/**
 * @param {any} evt - a normalised event.
 * @returns {string} the folded VEVENT block.
 */
export function serialiseEvent(evt: ICalEvent): string {
  const lines = ['BEGIN:VEVENT'];
  const uid = evt.providerId || makeUid();
  lines.push(`UID:${uid}`);
  if (evt.sequence != null) lines.push(`SEQUENCE:${evt.sequence}`);

  if (evt.allDay) {
    lines.push(`DTSTART;VALUE=DATE:${_formatDt(evt.startDate, true)}`);
    if (evt.endDate) lines.push(`DTEND;VALUE=DATE:${_formatDt(evt.endDate, true)}`);
  } else {
    const start = _formatDt(evt.startDate, false);
    const end = _formatDt(evt.endDate, false);
    // Force UTC suffix to keep things simple — we coerce on parse too.
    if (start) lines.push(`DTSTART:${start.endsWith('Z') ? start : `${start}Z`}`);
    if (end) lines.push(`DTEND:${end.endsWith('Z') ? end : `${end}Z`}`);
  }

  if (evt.title) lines.push(`SUMMARY:${_escText(evt.title)}`);
  if (evt.description) lines.push(`DESCRIPTION:${_escText(evt.description)}`);
  if (evt.location) lines.push(`LOCATION:${_escText(evt.location)}`);
  if (evt.rrule) lines.push(`RRULE:${evt.rrule}`);
  if (evt.recurrenceId)
    lines.push(
      `RECURRENCE-ID:${_formatDt(evt.recurrenceId, !!evt.allDay)}${evt.allDay ? '' : 'Z'}`
    );
  if (evt.exdate?.length) {
    for (const ex of evt.exdate) {
      lines.push(`EXDATE:${_formatDt(ex, !!evt.allDay)}${evt.allDay ? '' : 'Z'}`);
    }
  }

  // Touch LAST-MODIFIED so servers see this as a real edit. `new Date()` is
  // always a valid instant, so _formatDt never returns null here.
  const now = _formatDt(new Date().toISOString(), false);
  lines.push(`LAST-MODIFIED:${now}`);
  lines.push(`DTSTAMP:${now}`);

  // Preserve unknown properties verbatim.
  if (evt._extras?.length) {
    for (const x of evt._extras) {
      const paramStr = Object.keys(x.params || {})
        .map((k) => `;${k}=${x.params[k]}`)
        .join('');
      lines.push(`${x.name}${paramStr}:${x.value}`);
    }
  }

  lines.push('END:VEVENT');
  return lines.map(_fold).join('\r\n');
}

// ── Build a full VCALENDAR wrapper (one VEVENT per object) ──
/**
 * @param {any} eventOrEvents - one event or an array of events.
 * @param {any} [opts] - { prodid? }
 * @returns {string} a full VCALENDAR document.
 */
export function buildICS(
  eventOrEvents: ICalEvent | ICalEvent[],
  opts?: { prodid?: string }
): string {
  const list = Array.isArray(eventOrEvents) ? eventOrEvents : [eventOrEvents];
  const prodid = opts?.prodid || '-//Ariadne Notes//Calendar 2.0//EN';
  const head = ['BEGIN:VCALENDAR', 'VERSION:2.0', `PRODID:${prodid}`, 'CALSCALE:GREGORIAN'];
  const tail = ['END:VCALENDAR'];
  const body = list.map(serialiseEvent);
  return `${[...head, ...body, ...tail].join('\r\n')}\r\n`;
}

/**
 * Mint a fresh iCalendar UID (`<base36 ts>-<8 base36 rand>@ariadnenotes`).
 * Stable, sortable, collision-resistant enough for personal calendars. Used as
 * the serialiser fallback for events lacking a UID, and by caldav-provider when
 * creating a brand-new event.
 * @returns {string} a synthetic UID.
 */
export function makeUid(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 10);
  return `${ts}-${rand}@ariadnenotes`;
}

// ── RRULE shorthand round-trip ─────────────────────────────────────────
// Markdown surfaces a compact `~weekly` / `~daily` / `~monthly` shorthand
// alongside the verbose `~rrule:FREQ=…`. parseRruleShorthand maps the
// friendly forms to RFC 5545 RRULE strings; formatRruleShorthand collapses
// common patterns back so the markdown stays terse.
/**
 * @param {string} s - a friendly recurrence shorthand.
 * @returns {string | null} the RFC 5545 RRULE, or null.
 */
export function parseRruleShorthand(s: string): string | null {
  if (!s) return null;
  const lower = s.toLowerCase().trim();
  if (lower === 'daily') return 'FREQ=DAILY';
  if (lower === 'weekly') return 'FREQ=WEEKLY';
  if (lower === 'monthly') return 'FREQ=MONTHLY';
  if (lower === 'yearly' || lower === 'annually') return 'FREQ=YEARLY';
  if (lower.startsWith('every ')) {
    // "every 2 weeks", "every 3 days"
    const m = /^every\s+(\d+)\s+(day|week|month|year)s?$/.exec(lower);
    if (m) {
      const unit = m[2];
      const freq =
        unit === 'day'
          ? 'DAILY'
          : unit === 'week'
            ? 'WEEKLY'
            : unit === 'month'
              ? 'MONTHLY'
              : 'YEARLY';
      return `FREQ=${freq};INTERVAL=${m[1]}`;
    }
  }
  return null;
}

// Parse an RFC 5545 RRULE string ("FREQ=WEEKLY;INTERVAL=2;…") into a
// `{ FREQ, INTERVAL, … }` parts map. Shared by formatRruleShorthand,
// expandRrule, and microsoft-provider's RRULE→Graph-pattern converter (via the
// ICalCodec aggregate) so the split/filter idiom lives in exactly one place.
function _rruleParts(rrule: string): Record<string, string> {
  return Object.fromEntries(
    String(rrule || '')
      .split(';')
      .map((p) => p.split('='))
      .filter((p) => p.length === 2)
  );
}

/**
 * @param {string} rrule
 * @returns {string | null} a friendly shorthand, or null if none fits.
 */
export function formatRruleShorthand(rrule: string): string | null {
  if (!rrule) return null;
  const parts = _rruleParts(rrule);
  if (Object.keys(parts).length === 1 && parts.FREQ) {
    const m: Record<string, string> = {
      DAILY: 'daily',
      WEEKLY: 'weekly',
      MONTHLY: 'monthly',
      YEARLY: 'yearly',
    };
    return m[parts.FREQ] || null;
  }
  if (parts.FREQ && parts.INTERVAL && Object.keys(parts).length === 2) {
    const unit = (
      {
        DAILY: 'day',
        WEEKLY: 'week',
        MONTHLY: 'month',
        YEARLY: 'year',
      } as Record<string, string>
    )[parts.FREQ];
    if (unit) return `every ${parts.INTERVAL} ${unit}${parts.INTERVAL === '1' ? '' : 's'}`;
  }
  return null;
}

// ── RRULE expander ─────────────────────────────────────────────────────
// Generates concrete occurrences for the common FREQ=DAILY/WEEKLY/MONTHLY/
// YEARLY cases with INTERVAL, UNTIL, COUNT, BYDAY. Anything beyond that
// (BYSETPOS, RDATE, multi-part BYxxx) returns just the master so the schedule
// view still shows something.
//
// Recurrence advances on the WALL-CLOCK in the event's SOURCE FRAME, then each
// occurrence is re-resolved to an instant — NOT advanced as a fixed UTC delta.
// Advancing in UTC freezes the master's offset, so every occurrence past a DST
// transition drifts by the offset delta (a 09:00 America/New_York weekly event
// reading 10:00 after spring-forward). The frame is:
//   • allday   — UTC-anchored DATE, advance by whole days
//   • tzid     — DTSTART;TZID=…  → view the instant in the IANA zone, advance
//                the wall-clock, re-resolve each occurrence (DST-aware)
//   • utc      — a Z or explicit-offset instant → advance in UTC (no zone info
//                to do better; matches Google/Microsoft normalised output)
//   • floating — naive local wall-clock (unknown TZID) → advance the fields

function _isLeap(y: number): boolean {
  return y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0);
}
function _daysInMonth(y: number, mo: number): number {
  return [31, _isLeap(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][mo - 1];
}
function _validDate(y: number, mo: number, d: number): boolean {
  return mo >= 1 && mo <= 12 && d >= 1 && d <= _daysInMonth(y, mo);
}
function _cmpDate(y1: number, m1: number, d1: number, y2: number, m2: number, d2: number): number {
  return y1 - y2 || m1 - m2 || d1 - d2;
}
function _weekday(y: number, mo: number, d: number): number {
  return new Date(Date.UTC(y, mo - 1, d)).getUTCDay();
}
// Advance a calendar date by whole days using pure UTC math (DST-free).
function _addDays(y: number, mo: number, d: number, days: number): [number, number, number] {
  const t = new Date(Date.UTC(y, mo - 1, d) + days * 86400000);
  return [t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate()];
}
// Advance month-of-year by `add`, keeping the canonical day-of-month (so a
// day-31 series stays on the 31st and silently skips short months rather than
// drifting forward onto the 1st/3rd).
function _addMonths(y: number, mo: number, d: number, add: number): [number, number, number] {
  const total = y * 12 + (mo - 1) + add;
  return [Math.floor(total / 12), (((total % 12) + 12) % 12) + 1, d];
}

// Starting wall-clock fields {y, mo(1-12), d, h, mi, s} in the given frame.
function _wallFields(startStr: string, frame: string, tzid: string | null) {
  if (frame === 'tzid' && tzid) {
    const ms = Date.parse(startStr);
    if (Number.isNaN(ms)) return null;
    try {
      const dtf = _tzFormatter(tzid);
      const p: any = {};
      for (const part of dtf.formatToParts(new Date(ms))) p[part.type] = part.value;
      return { y: +p.year, mo: +p.month, d: +p.day, h: +p.hour % 24, mi: +p.minute, s: +p.second };
    } catch {
      return null;
    }
  }
  if (frame === 'utc') {
    // Z or explicit-offset string — the instant is fully specified; read its
    // UTC wall-clock so we advance in UTC.
    const ms = Date.parse(startStr);
    if (Number.isNaN(ms)) return null;
    const dt = new Date(ms);
    return {
      y: dt.getUTCFullYear(),
      mo: dt.getUTCMonth() + 1,
      d: dt.getUTCDate(),
      h: dt.getUTCHours(),
      mi: dt.getUTCMinutes(),
      s: dt.getUTCSeconds(),
    };
  }
  // allday + floating: the numeric fields in the string ARE the wall-clock.
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}):(\d{2}))?/.exec(startStr);
  if (!m) return null;
  return { y: +m[1], mo: +m[2], d: +m[3], h: +(m[4] || 0), mi: +(m[5] || 0), s: +(m[6] || 0) };
}

// Add `durMs` to a floating (no-Z) wall-clock ISO, returning a floating ISO.
function _floatingPlus(iso: string, durMs: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/.exec(iso);
  if (!m) return iso;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]) + durMs);
  const p = (v: number, n = 2) => String(v).padStart(n, '0');
  return `${p(d.getUTCFullYear(), 4)}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}T${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}.000`;
}

/**
 * @param {any} master - a recurring master event.
 * @param {any} [range] - { from?, to? } ISO bounds to clip occurrences.
 * @returns {any[]} concrete occurrences within the range.
 */
export function expandRrule(master: ICalEvent, range: any): ICalEvent[] {
  if (!master?.rrule || !master.startDate) {
    return master ? [master] : [];
  }
  const parts = _rruleParts(master.rrule);
  const freq = parts.FREQ;
  if (!freq) return [master];
  const interval = Math.max(1, parseInt(parts.INTERVAL || '1', 10));
  const count = parts.COUNT ? parseInt(parts.COUNT, 10) : Infinity;
  const until = parts.UNTIL ? _parseDt(parts.UNTIL, {}).iso : null;
  const untilTs = until ? Date.parse(until) : Infinity;
  const from = range?.from ? Date.parse(range.from) : -Infinity;
  const to = range?.to ? Date.parse(range.to) : Infinity;

  const tzid = master._startTzid || null;
  const startStr = String(master.startDate);
  const frame = master.allDay
    ? 'allday'
    : tzid
      ? 'tzid'
      : /(?:Z|[+-]\d{2}:?\d{2})$/.test(startStr)
        ? 'utc'
        : 'floating';

  const _startFields = _wallFields(startStr, frame, tzid);
  if (!_startFields) return [master];
  const start = _startFields; // non-null binding so the nested closures narrow

  // Duration preserved as elapsed ms (a fixed delta reproduces the wall-clock
  // duration on either side of a DST change for normal sub-day events).
  const startInstant = Date.parse(master.startDate);
  const endInstant = Date.parse(master.endDate ?? '');
  const durMs = Number.isNaN(endInstant)
    ? master.allDay
      ? 86400000
      : 0
    : endInstant - startInstant;

  const exdates = new Set(
    (master.exdate || []).map((x: any) => Date.parse(x)).filter((n: any) => !Number.isNaN(n))
  );
  const byDay = parts.BYDAY ? parts.BYDAY.split(',') : null;
  const dayCodes = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

  const out: any[] = [];
  let n = 0;
  const HARD_CAP = 1000; // safety net for runaway rules
  let safety = 0;

  // Resolve wall-clock (y,mo,d + the fixed start time) → { ts, iso } in the
  // frame, or null if the calendar date is invalid (day 31 in a 30-day month,
  // Feb 29 off a leap year, or a DST gap the zone can't represent).
  function _resolve(y: number, mo: number, d: number): { ts: number; iso: string } | null {
    if (!_validDate(y, mo, d)) return null;
    if (frame === 'allday') {
      const ts = Date.UTC(y, mo - 1, d);
      return { ts, iso: new Date(ts).toISOString() };
    }
    if (frame === 'utc') {
      const ts = Date.UTC(y, mo - 1, d, start.h, start.mi, start.s);
      return { ts, iso: new Date(ts).toISOString() };
    }
    if (frame === 'tzid') {
      const iso = _zonedToUtcIso(y, mo, d, start.h, start.mi, start.s, tzid);
      if (iso == null) return null;
      const ts = Date.parse(iso);
      return Number.isNaN(ts) ? null : { ts, iso };
    }
    const p = (v: number, len = 2) => String(v).padStart(len, '0');
    const iso = `${p(y, 4)}-${p(mo)}-${p(d)}T${p(start.h)}:${p(start.mi)}:${p(start.s)}.000`;
    return { ts: Date.parse(iso), iso };
  }

  // Emit the occurrence at (y,mo,d). Returns whether it counts toward COUNT
  // (a valid, in-window-or-not date — EXDATE'd instances count but aren't
  // pushed; invalid dates don't count) and whether iteration should stop.
  function _emit(y: number, mo: number, d: number): { counted: boolean; stop: boolean } {
    const r = _resolve(y, mo, d);
    if (!r) return { counted: false, stop: false };
    if (r.ts > untilTs) return { counted: false, stop: true };
    if (!exdates.has(r.ts) && r.ts >= from && r.ts <= to) {
      const endIso =
        frame === 'floating' ? _floatingPlus(r.iso, durMs) : new Date(r.ts + durMs).toISOString();
      out.push({ ...master, startDate: r.iso, endDate: endIso, recurrenceId: r.iso });
    }
    return { counted: true, stop: false };
  }

  let cur: [number, number, number] = [start.y, start.mo, start.d];
  let step = 0;
  while (n < count && safety++ < HARD_CAP) {
    let stop = false;
    if (freq === 'WEEKLY' && byDay && byDay.length) {
      // Emit each requested weekday within the week of the cursor (anchored on
      // the week's Sunday), skipping days before DTSTART.
      const [sy, smo, sd] = _addDays(cur[0], cur[1], cur[2], -_weekday(cur[0], cur[1], cur[2]));
      for (let i = 0; i < 7 && n < count; i++) {
        const [dy, dmo, dd] = _addDays(sy, smo, sd, i);
        if (_cmpDate(dy, dmo, dd, start.y, start.mo, start.d) < 0) continue;
        if (byDay.indexOf(dayCodes[_weekday(dy, dmo, dd)]) === -1) continue;
        const e = _emit(dy, dmo, dd);
        if (e.stop) {
          stop = true;
          break;
        }
        if (e.counted) n++;
      }
    } else {
      const e = _emit(cur[0], cur[1], cur[2]);
      if (e.stop) stop = true;
      else if (e.counted) n++;
    }
    if (stop) break;

    step += interval;
    if (freq === 'DAILY') cur = _addDays(start.y, start.mo, start.d, step);
    else if (freq === 'WEEKLY') cur = _addDays(start.y, start.mo, start.d, step * 7);
    else if (freq === 'MONTHLY') cur = _addMonths(start.y, start.mo, start.d, step);
    else if (freq === 'YEARLY') cur = [start.y + step, start.mo, start.d];
    else break;

    // The cursor advances monotonically; once it's past both the range end and
    // UNTIL, no further occurrence can qualify.
    const probe = _resolve(cur[0], cur[1], cur[2]);
    if (probe && probe.ts > to && probe.ts > untilTs) break;
  }

  return out;
}

// Phase 4.7: real ESM. `ICalCodec` is the aggregate the schedule modules
// destructure (`Codec.parseICS(...)`) — calendar-sync.ts and caldav-provider.ts
// import it directly as a named ESM export. (The former `window.ICalCodec`
// runtime mirror was removed: no consumer ever read it off window.)
export const ICalCodec = {
  parseICS,
  buildICS,
  serialiseEvent,
  parseRruleShorthand,
  formatRruleShorthand,
  expandRrule,
  makeUid,
  _rruleParts,
  _parseDt,
  _formatDt,
  _escText,
  _unescText,
  _fold,
  _unfold,
};
